import { useCallback, useEffect, useState } from 'react';

/**
 * True when the observed element is narrower than `breakpoint`. For dropping
 * controls that stop being usable below a certain width rather than letting
 * them compress into a sliver.
 *
 * Observe a container whose width does NOT depend on the thing being hidden —
 * a parent, not the control itself. Measuring the control feeds back: hide it,
 * it loses width, the condition re-fires, and the layout oscillates.
 *
 * Returns a boolean rather than the width on purpose. React bails out of a
 * setState that doesn't change the value, so a subscriber re-renders only when
 * the threshold is actually crossed — not on every pixel of a drag-resize.
 * That matters here because these mount per workout table.
 */

/** One observer for every subscriber, rather than one per hook call. A single
 * ResizeObserver with N targets is materially cheaper than N observers, and the
 * browser batches its callback into one delivery. The calendar list is
 * virtualized so N is bounded by the viewport, but week view mounts a column
 * per day and this keeps that flat. */
const callbacks = new WeakMap();
let sharedObserver = null;

const getSharedObserver = () => {
  if (!sharedObserver) {
    sharedObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        callbacks.get(entry.target)?.forEach((measure) => measure(entry.target));
      });
    });
  }
  return sharedObserver;
};

/** A Set of callbacks per element, not one: the same element can be watched at
 * more than one breakpoint - a pre-save footer drops its pills at one width and
 * its Options dropdown at a narrower one. With a single callback per element
 * the second subscriber would overwrite the first, and the first to unmount
 * would unobserve the element out from under the other.
 *
 * The element is observed when its first subscriber arrives and released when
 * its last one leaves. */
const subscribe = (el, measure) => {
  let elCallbacks = callbacks.get(el);
  if (!elCallbacks) {
    elCallbacks = new Set();
    callbacks.set(el, elCallbacks);
    getSharedObserver().observe(el);
  }
  elCallbacks.add(measure);

  return () => {
    elCallbacks.delete(measure);
    if (elCallbacks.size) return;
    callbacks.delete(el);
    getSharedObserver().unobserve(el);
  };
};

const useIsNarrowerThan = (breakpoint) => {
  const [element, setElement] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);

  /** A callback ref, not an object ref: a pre-save table swaps its whole tree
   * for a saving placeholder on every autosave, so the element it hands back
   * afterwards is a different DOM node. An object ref mutates silently and the
   * effect would never re-run - it would go on observing the detached node and
   * never touch the new one. React calls this on every attach and detach.
   *
   * useCallback with no deps is load-bearing: an inline arrow would be a new
   * function each render, so React would detach and re-attach the ref every
   * time, and the setElement inside it would loop. */
  const ref = useCallback((node) => setElement(node), []);

  useEffect(() => {
    const el = element;
    if (!el) return undefined;

    /** A zero width is the absence of a measurement, not a narrow element, and
     * must not hide anything. Two ways to get one: a collapsed saved table
     * stays mounted under `display: none`, and jsdom has no layout engine at
     * all (its ResizeObserver is a no-op stub, so this initial read is the only
     * one tests ever get). Both should leave the controls alone. */
    const measure = (target) => {
      const { width } = target.getBoundingClientRect();
      if (!width) return;
      setIsNarrow(width < breakpoint);
    };
    measure(el);

    if (typeof ResizeObserver === 'undefined') return undefined;

    return subscribe(el, measure);
  }, [element, breakpoint]);

  return { ref, isNarrow };
};

export default useIsNarrowerThan;
